import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PageHeader from '../headers/Header';
import {
  X, User, Mail, Calendar, Search, Filter, ChevronLeft, ChevronRight,
  FileText, FileSpreadsheet, ArrowUp, ArrowDown, Archive, Clock, UserX,
  MapPin, Briefcase, Building2, Users, RotateCcw
} from 'lucide-react';
import { getEmployeeImage, getAllOffboardings } from '../../services/HRstaff';
import { getAllInactiveEmployees } from '../../services/EmployeeService';
import { formatPositionsDisplay } from '../../services/positionAPIs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import marabesLogo from '../../assets/images/logos/Marabes.png';


import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Pagination,
  Tooltip,
  useTheme,
  LinearProgress,
  Alert,
  Fade
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

if (typeof jsPDF === 'function' && !jsPDF.prototype.autoTable) {
  jsPDF.prototype.autoTable = autoTable;
}

const getBase64FromUrl = (url) => {
  return new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
  });
};

const EMPLOYEES_PER_PAGE = 10;

function InactiveEmployees() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [selectedOffice, setSelectedOffice] = useState('All');
  const [selectedJob, setSelectedJob] = useState('All');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeTab, setActiveTab] = useState('employment');
  const [currentPage, setCurrentPage] = useState(1);
  const [profileImages, setProfileImages] = useState({});
  const [offboardingMap, setOffboardingMap] = useState({});

  // New state for date filters
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const departments = ['All', 'IT', 'HR', 'Planning', 'Accounting', 'Customer_services', 'Billing'];
  const offices = ['All', 'Nador', 'Tangier', 'Selouane', 'Casablanca'];
  const jobTitles = [
    'All', 'Director', 'HR Manager', 'Project Manager', 'Customer Service', 'HR',
    'Manager', 'Account Manager', 'Quality Reviewer', 'Bookkeeper',
    'Planner', 'Personal Assistance', 'Software Engineer', 'Marketing', 'Client Adviser', 'IT Professional'
  ];

  // Month names array
  const MONTH_NAMES = ['All', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // Helper function to format underscored text
  const formatUnderscoredText = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  const fetchSingleImage = useCallback(async (employeeId) => {
    try {
      if (!employeeId || profileImages[employeeId]) return null;
      const imageBlob = await getEmployeeImage(employeeId);
      if (imageBlob) return { employeeId, imageUrl: imageBlob };
      return null;
    } catch (error) {
      console.warn(`Failed to fetch image for employee ${employeeId}:`, error);
      return null;
    }
  }, [profileImages]);

  const fetchEmployeeImages = useCallback(async (employeeList) => {
    if (!employeeList?.length) return;
    const employeesToFetch = employeeList.filter(emp => emp.employeeId && !profileImages[emp.employeeId]);
    if (employeesToFetch.length === 0) return;

    try {
      const batchSize = 3;
      for (let i = 0; i < employeesToFetch.length; i += batchSize) {
        const batch = employeesToFetch.slice(i, i + batchSize);
        const batchPromises = batch.map(emp => fetchSingleImage(emp.employeeId));
        const batchResults = await Promise.allSettled(batchPromises);
        const successfulResults = batchResults
          .filter(result => result.status === 'fulfilled' && result.value)
          .map(result => result.value);

        if (successfulResults.length > 0) {
          setProfileImages(prev => {
            const updated = { ...prev };
            successfulResults.forEach(res => {
              updated[res.employeeId] = res.imageUrl;
            });
            return updated;
          });
        }
      }
    } catch (err) {
      console.error('Image fetch error:', err);
    }
  }, [profileImages, fetchSingleImage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [employeesData, offboardings] = await Promise.all([
          getAllInactiveEmployees(),
          getAllOffboardings()
        ]);

        fetch('http://localhost:8001/dump', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(employeesData) }).catch(e => console.error(e));

        const offMap = {};
        offboardings.forEach(record => {
          let empId = null;
          if (record.employee?.employeeId) {
            empId = record.employee.employeeId;
          } else {
            const fullName = `${record.employeeFirstName || ''} ${record.employeeLastName || ''}`.trim();
            const matchedEmp = employeesData.find(e =>
              `${e.firstName || ''} ${e.lastName || ''}`.trim() === fullName
            );
            empId = matchedEmp?.employeeId || matchedEmp?.id;
          }

          if (empId) {
            offMap[empId] = {
              exitDate: record.exitDate || null,
              reason: record.reasonForDeparture || 'N/A',
              notes: record.additionalNotes || 'N/A'
            };
          }
        });

        setOffboardingMap(offMap);

        const extractInactivePositions = (emp) => {
          if (!emp) return 'N/A';
          if (emp.positions && Array.isArray(emp.positions) && emp.positions.length > 0) {
            const valid = emp.positions.filter(p => p && p.positionName);
            if (valid.length > 0) return valid.map(p => p.positionName).join('; ');
          }
          if (emp.contract && Array.isArray(emp.contract)) {
            for (const c of emp.contract) {
              if (c.employee?.positions && Array.isArray(c.employee.positions)) {
                const valid = c.employee.positions.filter(p => p && p.positionName);
                if (valid.length > 0) return valid.map(p => p.positionName).join('; ');
              }
            }
          }
          if (emp.payrolls && Array.isArray(emp.payrolls)) {
            for (const p of emp.payrolls) {
              if (p.employee?.positions && Array.isArray(p.employee.positions)) {
                const valid = p.employee.positions.filter(pos => pos && pos.positionName);
                if (valid.length > 0) return valid.map(pos => pos.positionName).join('; ');
              }
            }
          }
          if (emp.balances && Array.isArray(emp.balances)) {
            for (const b of emp.balances) {
              if (b.employee?.positions && Array.isArray(b.employee.positions)) {
                const valid = b.employee.positions.filter(pos => pos && pos.positionName);
                if (valid.length > 0) return valid.map(pos => pos.positionName).join('; ');
              }
            }
          }
          const single = emp.positionName || emp.position || emp.jobTitle || emp.job || emp.role || emp.title;
          if (single) return single;
          return 'N/A';
        };

        const transformedData = employeesData.map(employee => ({
          id: employee.employeeId || employee.id,
          firstName: employee.firstName,
          lastName: employee.lastName,
          name: `${employee.firstName} ${employee.lastName}`,
          email: employee.user?.email || employee.email || 'N/A',
          phone: employee.phone || 'N/A',
          department: formatUnderscoredText(employee.departmentName) || 'N/A',
          office: formatUnderscoredText(employee.officeName) || 'N/A',
          position: extractInactivePositions(employee),
          secondposition: '',
          dateOfJoining: employee.dateOfJoining || employee.joiningDate || employee.startDate,
          employeeId: employee.employeeId || employee.id,
          dob: employee.dob || '',
          gender: employee.gender || '',
          cin: employee.cin || '',
          cnss: employee.cnss || '',
          diploma: employee.diploma || '',
          role: employee.user?.role || employee.role || 'EMPLOYEE',
          exitDate: offMap[employee.employeeId]?.exitDate,
          offboardingReason: formatUnderscoredText(offMap[employee.employeeId]?.reason) || 'N/A',
          offboardingDescription: offMap[employee.employeeId]?.notes || 'N/A'
        }));

        setEmployees(transformedData);
      } catch (err) {
        setError('Failed to load inactive employees.');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    let dateObj = typeof date === 'string' ? new Date(date) : new Date(date);
    if (isNaN(dateObj.getTime())) {
      dateObj = new Date(Date.parse(date));
    }
    if (isNaN(dateObj.getTime())) return 'N/A';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculatePeriodWorked = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A';
    let start = typeof startDate === 'string' ? new Date(startDate) : new Date(startDate);
    let end = typeof endDate === 'string' ? new Date(endDate) : new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';
    const diffTime = Math.abs(end - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 31) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      let result = '';
      if (years > 0) result += `${years} year${years > 1 ? 's' : ''} `;
      if (months > 0) result += `${months} month${months > 1 ? 's' : ''}`;
      return result.trim();
    }
  };

  const getOffboardingStatusColor = (reason) => {
    const reasonLower = reason?.toLowerCase() || '';
    if (reasonLower.includes('resign') || reasonLower.includes('resignation')) return 'error'; // Resigned often viewed as loss/negative in some contexts, or warning
    if (reasonLower.includes('terminate') || reasonLower.includes('termination')) return 'error';
    if (reasonLower.includes('retire') || reasonLower.includes('retirement')) return 'info';
    return 'default';
  };

  // Get available years from employee data
  const availableYears = useMemo(() => {
    const years = new Set();
    employees.forEach(emp => {
      if (emp.exitDate) {
        const year = new Date(emp.exitDate).getFullYear();
        if (!isNaN(year)) years.add(year.toString());
      }
    });
    return ['All', ...Array.from(years).sort((a, b) => parseInt(b) - parseInt(a))];
  }, [employees]);

  const sortedEmployees = useMemo(() => {
    let sortableItems = [...employees];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'dateOfJoining' || sortConfig.key === 'exitDate') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [employees, sortConfig]);

  const filteredEmployees = useMemo(() => {
    return sortedEmployees.filter((emp) => {
      // Existing filters
      const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.phone.includes(searchTerm);
      const matchesDepartment = selectedDepartment === 'All' || emp.department === selectedDepartment;
      const matchesOffice = selectedOffice === 'All' || emp.office === selectedOffice;
      const matchesJob = selectedJob === 'All' || emp.position === selectedJob;

      // Date filters
      let matchesYear = true;
      let matchesMonth = true;
      let matchesDateRange = true;

      // Check if employee has an exit date
      if (emp.exitDate) {
        const exitDate = new Date(emp.exitDate);
        const isValidDate = !isNaN(exitDate.getTime());

        if (isValidDate) {
          // Year filter
          if (selectedYear !== 'All') {
            matchesYear = exitDate.getFullYear().toString() === selectedYear;
          }

          // Month filter
          if (selectedMonth !== 'All') {
            const monthIndex = MONTH_NAMES.indexOf(selectedMonth) - 1; // Adjust for 'All' at index 0
            if (monthIndex >= 0) {
              matchesMonth = exitDate.getMonth() === monthIndex;
            }
          }

          // Date range filter
          if (dateRange.start && dateRange.end) {
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            // Reset time for accurate date comparison
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            matchesDateRange = exitDate >= startDate && exitDate <= endDate;
          }
        }
      } else if (selectedYear !== 'All' || selectedMonth !== 'All' || (dateRange.start && dateRange.end)) {
        // If no exit date but filters are applied, exclude the employee
        return false;
      }

      return matchesSearch && matchesDepartment && matchesOffice && matchesJob &&
        matchesYear && matchesMonth && matchesDateRange;
    });
  }, [sortedEmployees, searchTerm, selectedDepartment, selectedOffice, selectedJob,
    selectedYear, selectedMonth, dateRange, MONTH_NAMES]);

  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * EMPLOYEES_PER_PAGE;
    return filteredEmployees.slice(startIndex, startIndex + EMPLOYEES_PER_PAGE);
  }, [filteredEmployees, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE);
  }, [filteredEmployees.length]);

  useEffect(() => {
    if (paginatedEmployees.length > 0) {
      const needsImages = paginatedEmployees.filter(emp => emp.employeeId && !profileImages[emp.employeeId]);
      if (needsImages.length > 0) fetchEmployeeImages(needsImages);
    }
  }, [paginatedEmployees, profileImages, fetchEmployeeImages]);

  const exportToPDF = async () => {
    try {
      // Use landscape 'l' for better table fitting with more columns
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 14;
      const pw = doc.internal.pageSize.width;

      // Fetch logos
      const [marabesBase64, axoBase64] = await Promise.all([
        getBase64FromUrl(marabesLogo),
        getBase64FromUrl('/cLogo.png')
      ]);

      // Pre-fetch employee base64 images
      const employeeBase64Images = {};
      await Promise.all(
        filteredEmployees.map(async (emp) => {
          const empId = emp.employeeId || emp.id;
          if (profileImages[empId]) {
            const b64 = await getBase64FromUrl(profileImages[empId]);
            if (b64) employeeBase64Images[empId] = b64;
          }
        })
      );

      const contentWidth = pw - (margin * 2);

      // Add Logos
      if (marabesBase64) doc.addImage(marabesBase64, 'PNG', margin, 10, 35, 12);
      if (axoBase64) doc.addImage(axoBase64, 'PNG', pw - margin - 35, 10, 35, 12);

      // Draw horizontal line
      doc.setDrawColor(63, 81, 181);
      doc.setLineWidth(0.5);
      doc.line(margin, 25, pw - margin, 25);

      // Draw light blue background block
      doc.setFillColor(232, 245, 255);
      doc.rect(margin, 30, contentWidth, 14, 'F');

      const textY = 39;

      // Date on the left
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, margin + 3, textY);

      // Title in the center
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 58, 139);
      doc.text('Inactive Employees Archive', pw / 2, textY, { align: 'center' });

      // Total Records on the right
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      const recordsText = `Total Records: ${filteredEmployees.length}`;
      const textWidth = doc.getTextWidth(recordsText);
      doc.text(recordsText, margin + contentWidth - 3 - textWidth, textY);

      const columns = [
        { header: 'Photo', dataKey: 'photo' },
        { header: 'Full Name', dataKey: 'name' },
        { header: 'Job Title', dataKey: 'position' },
        { header: 'Department', dataKey: 'department' },
        { header: 'Office', dataKey: 'office' },
        { header: 'Description', dataKey: 'offboardingDescription' },
        { header: 'Start Date', dataKey: 'startDate' },
        { header: 'Exit Date', dataKey: 'exitDate' },
        { header: 'Period Worked', dataKey: 'periodWorked' }
      ];

      const tableData = filteredEmployees.map(emp => ({
        id: emp.employeeId || emp.id,
        photo: '', // Placeholder for autoTable didDrawCell
        name: `${emp.firstName} ${emp.lastName}`,
        position: emp.position,
        department: emp.department,
        office: typeof emp.office === 'object' ? (emp.office?.name || 'N/A') : (emp.office || 'N/A'),
        offboardingDescription: emp.offboardingDescription,
        startDate: formatDateDDMMYYYY(emp.dateOfJoining),
        exitDate: formatDateDDMMYYYY(emp.exitDate),
        periodWorked: calculatePeriodWorked(emp.dateOfJoining, emp.exitDate)
      }));

      const now = new Date();
      const exportDate = formatDateDDMMYYYY(now);

      autoTable(doc, {
        startY: 48,
        columns: columns,
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], fontSize: 8, fontStyle: 'bold', cellPadding: 2, halign: 'center' },
        styles: { font: 'helvetica', fontSize: 7, cellPadding: 2, valign: 'middle', minCellHeight: 12 },
        columnStyles: {
          photo: { cellWidth: 15, halign: 'center' },
          name: { cellWidth: 30 }
        },
        didDrawCell: (data) => {
          if (data.column.dataKey === 'photo' && data.section === 'body') {
            const rowData = tableData[data.row.index];
            const empId = rowData.id;
            const b64 = employeeBase64Images[empId];
            if (b64) {
              const imgDim = 10;
              const x = data.cell.x + (data.cell.width - imgDim) / 2;
              const y = data.cell.y + (data.cell.height - imgDim) / 2;
              try {
                // Add employee image to cell
                doc.addImage(b64, 'PNG', x, y, imgDim, imgDim);
              } catch (e) {
                console.error('Failed to add image to PDF for', empId, e);
              }
            }
          }
        },
        didDrawPage: (data) => {
          // Footer
        }
      });

      doc.save('inactive_employees_archive.pdf');
    } catch (error) {
      console.error('PDF Export Error:', error);
      alert('Failed to generate PDF: ' + (error.message || 'Unknown error'));
    }
  };

  const exportToExcel = () => {
    try {
      const columns = [
        { header: 'Full Name', key: 'name' },
        { header: 'Work Email', key: 'email' },
        { header: 'Phone Number', key: 'phone' },
        { header: 'Job Title', key: 'position' },
        { header: 'Department', key: 'department' },
        { header: 'Office', key: 'office' },
        { header: 'Description', key: 'offboardingDescription' },
        { header: 'Start Date', key: 'startDate' },
        { header: 'Exit Date', key: 'exitDate' },
        { header: 'Period Worked', key: 'periodWorked' }
      ];

      const worksheetData = filteredEmployees.map(emp => ({
        name: `${emp.firstName} ${emp.lastName}`,
        email: emp.email,
        phone: emp.phone,
        position: emp.position,
        department: emp.department,
        office: typeof emp.office === 'object' ? (emp.office?.name || 'N/A') : (emp.office || 'N/A'),
        offboardingReason: emp.offboardingReason,
        offboardingDescription: emp.offboardingDescription,
        startDate: formatDateDDMMYYYY(emp.dateOfJoining),
        exitDate: formatDateDDMMYYYY(emp.exitDate),
        periodWorked: calculatePeriodWorked(emp.dateOfJoining, emp.exitDate)
      }));

      const ws = XLSX.utils.json_to_sheet(worksheetData, { header: columns.map(c => c.key) });
      XLSX.utils.sheet_add_aoa(ws, [columns.map(c => c.header)], { origin: "A1" });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inactive_Employees");
      XLSX.writeFile(wb, "inactive_employees_archive.xlsx");
    } catch (error) {
      console.error('Excel Export Error:', error);
      alert('Failed to generate Excel file: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleImageClick = (employeeId) => {
    navigate(`/inactive-profile/${employeeId}`);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('All');
    setSelectedOffice('All');
    setSelectedJob('All');
    setSelectedYear('All');
    setSelectedMonth('All');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  const renderHeaderCell = (label, sortKey) => (
    <TableCell
      onClick={() => handleSort(sortKey)}
      sx={{ fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {sortConfig.key === sortKey && (
          sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
        )}
      </Box>
    </TableCell>
  );

  if (loading && employees.length === 0) return (
    <Box sx={{ width: '100%', mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <LinearProgress sx={{ width: '100%', maxWidth: 500, borderRadius: 2, height: 8 }} />
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="primary">Opening Archive Vault...</Typography>
        <Typography variant="body2" color="text.secondary">Loading inactive employee records</Typography>
      </Box>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <PageHeader
        title="Inactive Employees"
        subtitle="Historical records of former employees"
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              icon={<Archive size={14} />}
              label={`${filteredEmployees.length} archived profiles`}
              size="big"
              sx={{ height: '40px', bgcolor: 'primary.dark', color: 'primary.contrastText', fontWeight: 'bold', mr: 2, display: { xs: 'none', sm: 'flex' } }}
            />
            <Button
              variant="contained"
              color="error"
              startIcon={<FileText size={16} />}
              onClick={exportToPDF}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', boxShadow: 2 }}
            >
              PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<FileSpreadsheet size={16} />}
              onClick={exportToExcel}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', boxShadow: 2 }}
            >
              Excel
            </Button>
          </Box>
        }
      />

      {/* Mobile Chip */}
      <Box sx={{ mb: 3, display: { xs: 'block', sm: 'none' } }}>
        <Chip
          icon={<Archive size={14} />}
          label={`${filteredEmployees.length} archived profiles`}
          size="small"
          sx={{ bgcolor: 'primary.dark', color: 'primary.contrastText', fontWeight: 'bold', width: '100%' }}
        />
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Filter size={18} /> Archive Search & Filters
          </Typography>
          <Button onClick={resetFilters} size="small" variant="outlined" color="error" startIcon={<RotateCcw size={16} />} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Reset Filters</Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Office</InputLabel>
              <Select
                value={selectedOffice}
                label="Office"
                startAdornment={<InputAdornment position="start"><MapPin size={16} style={{ color: '#1976d2', marginRight: -4 }} /></InputAdornment>}
                onChange={(e) => setSelectedOffice(e.target.value)}
              >
                {offices.map(office => <MenuItem key={office} value={office}>{formatUnderscoredText(office)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 155 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                startAdornment={<InputAdornment position="start"><Users size={16} style={{ color: '#1976d2', marginRight: -4 }} /></InputAdornment>}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                {departments.map(dept => <MenuItem key={dept} value={dept}>{formatUnderscoredText(dept)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Job Title</InputLabel>
              <Select
                value={selectedJob}
                label="Job Title"
                startAdornment={<InputAdornment position="start"><Briefcase size={16} style={{ color: '#1976d2', marginRight: -4 }} /></InputAdornment>}
                onChange={(e) => setSelectedJob(e.target.value)}
              >
                {jobTitles.map(job => <MenuItem key={job} value={job}>{formatUnderscoredText(job)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                startAdornment={<InputAdornment position="start"><Calendar size={16} style={{ color: '#1976d2', marginRight: -4 }} /></InputAdornment>}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {availableYears.map(year => <MenuItem key={year} value={year}>{year === 'All' ? 'All Years' : year}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small" sx={{ minWidth: 135 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                startAdornment={<InputAdornment position="start"><Calendar size={16} style={{ color: '#1976d2', marginRight: -4 }} /></InputAdornment>}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {MONTH_NAMES.map(month => <MenuItem key={month} value={month}>{month}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <DatePicker
                  label="Start Date"
                  format="dd/MM/yyyy"
                  value={dateRange.start ? new Date(dateRange.start) : null}
                  onChange={(val) => {
                    if (val && !isNaN(val.getTime())) {
                      const yr = val.getFullYear();
                      const mo = String(val.getMonth() + 1).padStart(2, '0');
                      const da = String(val.getDate()).padStart(2, '0');
                      setDateRange(prev => ({ ...prev, start: `${yr}-${mo}-${da}` }));
                    } else {
                      setDateRange(prev => ({ ...prev, start: '' }));
                    }
                  }}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <Typography variant="body2" color="text.secondary">to</Typography>
                <DatePicker
                  label="End Date"
                  format="dd/MM/yyyy"
                  value={dateRange.end ? new Date(dateRange.end) : null}
                  minDate={dateRange.start ? new Date(dateRange.start) : null}
                  onChange={(val) => {
                    if (val && !isNaN(val.getTime())) {
                      const yr = val.getFullYear();
                      const mo = String(val.getMonth() + 1).padStart(2, '0');
                      const da = String(val.getDate()).padStart(2, '0');
                      setDateRange(prev => ({ ...prev, end: `${yr}-${mo}-${da}` }));
                    } else {
                      setDateRange(prev => ({ ...prev, end: '' }));
                    }
                  }}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab
            icon={<Clock size={16} />}
            iconPosition="start"
            label="Employment"
            value="employment"
          />
          <Tab
            icon={<Briefcase size={16} />}
            iconPosition="start"
            label="Job"
            value="job"
          />
          <Tab
            icon={<User size={16} />}
            iconPosition="start"
            label="Personal"
            value="personal"
          />
        </Tabs>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: theme.palette.action.hover }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Photo</TableCell>
              {renderHeaderCell('Name', 'name')}
              {activeTab === 'personal' && (
                <>
                  {renderHeaderCell('Email', 'email')}
                  {renderHeaderCell('Phone', 'phone')}
                  <TableCell sx={{ fontWeight: 'bold' }}>CNSS</TableCell>
                </>
              )}
              {activeTab === 'job' && (
                <>
                  {renderHeaderCell('Position', 'position')}
                  {renderHeaderCell('Department', 'department')}
                  {renderHeaderCell('Office', 'office')}
                </>
              )}
              {activeTab === 'employment' && (
                <>
                  {renderHeaderCell('Started', 'dateOfJoining')}
                  {renderHeaderCell('Left', 'exitDate')}
                  {renderHeaderCell('Reason', 'offboardingDescription')}
                  {renderHeaderCell('Additional notes', 'offboardingReason')}
                  <TableCell sx={{ fontWeight: 'bold' }}>Worked Period</TableCell>
                </>
              )}
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEmployees.map((emp) => (
              <TableRow key={emp.id} hover>
                <TableCell>
                  <Avatar
                    src={profileImages[emp.employeeId] || undefined}
                    onClick={() => handleImageClick(emp.id)}
                    sx={{ cursor: 'pointer', width: 40, height: 40, border: '1px solid #e2e8f0' }}
                  >
                    {emp.firstName?.[0]}{emp.lastName?.[0]}
                  </Avatar>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="600">{emp.firstName} {emp.lastName}</Typography>
                    {/* <Chip
                      label={emp.offboardingReason}
                      size="small"
                      color={getOffboardingStatusColor(emp.offboardingReason)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    /> */}
                  </Box>
                </TableCell>

                {activeTab === 'personal' && (
                  <>
                    <TableCell>{emp.email}</TableCell>
                    <TableCell>{emp.phone}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {emp.cnss || 'N/A'}
                      </Typography>
                    </TableCell>
                  </>
                )}

                {activeTab === 'job' && (
                  <>
                    <TableCell><Typography variant="body2" fontWeight="500">{emp.position}</Typography></TableCell>
                    <TableCell><Chip label={emp.department} size="small" /></TableCell>
                    <TableCell>{emp.office}</TableCell>
                  </>
                )}

                {activeTab === 'employment' && (
                  <>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatDateDDMMYYYY(emp.dateOfJoining)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'error.main', fontWeight: 'bold' }}>{formatDateDDMMYYYY(emp.exitDate)}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {emp.offboardingReason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {emp.offboardingDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={calculatePeriodWorked(emp.dateOfJoining, emp.exitDate)}
                        size="small"
                        color="success"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                  </>
                )}

                <TableCell align="center">
                  <IconButton size="small" color="primary" onClick={() => navigate(`/inactive-profile/${emp.id}`)}>
                    <User size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedEmployees.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No records found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
}

export default InactiveEmployees;